import { useState, useEffect } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Checkbox,
  Text,
  useToast,
  Alert,
  AlertIcon,
  Box,
  Badge,
} from '@chakra-ui/react';

import { APISubmissionPolicyData } from 'types/api.types';
import {
  RUNNER_MC_AUTO,
  RUNNER_MC_HOME,
  WIP_STATUS_SIGNING_AWAITING_BROKER,
} from 'variables/constants';

interface LinkPoliciesModalProps {
  isOpen: boolean;
  onClose: () => void;
  policies: APISubmissionPolicyData[];
  onLinkBinders: (
    policyUuids: string[],
    policyGroupId?: string,
  ) => Promise<void>;
  isLinking: boolean;
  existingGroupId?: string | null;
}

const LinkPoliciesModal = ({
  isOpen,
  onClose,
  policies,
  onLinkBinders,
  isLinking,
  existingGroupId,
}: LinkPoliciesModalProps) => {
  const toast = useToast();
  const [selectedPolicies, setSelectedPolicies] = useState<string[]>([]);

  // Filter to only show linkable policies (Auto and Home, not already linked, not signed)
  const linkablePolicies = policies.filter(
    (policy) =>
      (policy.runner === RUNNER_MC_AUTO || policy.runner === RUNNER_MC_HOME) &&
      !policy.policy_group_id &&
      (!policy.wip_status ||
        policy.wip_status < WIP_STATUS_SIGNING_AWAITING_BROKER),
  );

  // Minimum required: 2 for new group, 1 for adding to existing
  const minRequired = existingGroupId ? 1 : 2;

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedPolicies([]);
    }
  }, [isOpen]);

  const handleTogglePolicy = (uuid: string) => {
    setSelectedPolicies((prev) =>
      prev.includes(uuid) ? prev.filter((p) => p !== uuid) : [...prev, uuid],
    );
  };

  const handleLink = async () => {
    if (selectedPolicies.length < minRequired) {
      toast({
        title: `Select at least ${minRequired} binder${
          minRequired > 1 ? 's' : ''
        }`,
        description: existingGroupId
          ? 'Select at least 1 binder to add to the group.'
          : 'You need to select at least 2 binders to link them together.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    try {
      await onLinkBinders(selectedPolicies, existingGroupId || undefined);
      onClose();
    } catch (error: any) {
      toast({
        title: 'Failed to link binders',
        description: error?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const getPolicyTypeLabel = (runner: number): string => {
    if (runner === RUNNER_MC_AUTO) return 'Auto';
    if (runner === RUNNER_MC_HOME) return 'Home';
    return 'Unknown';
  };

  const getPolicyIndex = (policy: APISubmissionPolicyData): number => {
    const sameTypePolicies = policies.filter((p) => p.runner === policy.runner);
    return sameTypePolicies.findIndex((p) => p.uuid === policy.uuid) + 1;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {existingGroupId ? 'Add to Linked Binders' : 'Link Binders'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Alert status="info" mb={4} borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontSize="sm">
                {existingGroupId
                  ? 'Add binders to the existing multi-binder group for combined signing.'
                  : 'Link Auto and Home binders together for combined signing. All linked binders will be signed as a single document.'}
              </Text>
            </Box>
          </Alert>

          {linkablePolicies.length < minRequired ? (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                {existingGroupId
                  ? 'No unlinked Auto or Home binders available to add.'
                  : 'You need at least 2 unlinked Auto or Home binders to create a multi-binder group.'}
              </Text>
            </Alert>
          ) : (
            <VStack spacing={3} align="stretch">
              <Text fontWeight="medium" mb={2}>
                {existingGroupId
                  ? 'Select binders to add:'
                  : 'Select binders to link:'}
              </Text>
              {linkablePolicies.map((policy) => (
                <Box
                  key={policy.uuid}
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={
                    selectedPolicies.includes(policy.uuid)
                      ? 'blue.500'
                      : 'gray.200'
                  }
                  bg={
                    selectedPolicies.includes(policy.uuid) ? 'blue.50' : 'white'
                  }
                  cursor="pointer"
                  onClick={() => handleTogglePolicy(policy.uuid)}
                  _hover={{ borderColor: 'blue.300' }}
                >
                  <Checkbox
                    isChecked={selectedPolicies.includes(policy.uuid)}
                    onChange={() => handleTogglePolicy(policy.uuid)}
                    colorScheme="blue"
                  >
                    <Box ml={2}>
                      <Text fontWeight="medium">
                        {getPolicyTypeLabel(policy.runner)}
                        {policy.client_name
                          ? ` - ${policy.client_name}`
                          : ` ${getPolicyIndex(policy)}`}
                        {policy.is_loa && ' (LOA)'}
                      </Text>
                      <Badge
                        colorScheme={
                          policy.runner === RUNNER_MC_AUTO ? 'blue' : 'green'
                        }
                        size="sm"
                      >
                        {getPolicyTypeLabel(policy.runner)}
                      </Badge>
                    </Box>
                  </Checkbox>
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleLink}
            isLoading={isLinking}
            isDisabled={
              selectedPolicies.length < minRequired ||
              linkablePolicies.length < minRequired
            }
          >
            {existingGroupId ? 'Add to Group' : 'Link Binders'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LinkPoliciesModal;
